<template lang="pug">
  v-tabs.yz-tabs(:class="tabsClasses" :style="tabsStyle" :value="value" @change="handleChange" v-bind="$attrs" v-on="$listeners")
    slot
</template>

<script>
export default {
  name: 'YzTabs',
  props: {
    value: {
      type: Number,
      default: 0
    },
    type: {
      type: String,
      default: 'line',
      validator: value => ['line', 'button'].includes(value)
    },
    color: {
      type: String,
      default: 'primary'
    },
    badgeColor: {
      type: String,
      default: null
    },
    badgeActiveColor: {
      type: String,
      default: null
    },
    buttonHoverColor: {
      type: String,
      default: 'neutral-200'
    }
  },
  computed: {
    tabsClasses() {
      return [
        `yz-tabs--${this.type}`,
        `yz-tabs--${this.color}`
      ]
    },
    tabsStyle() {
      const style = {}
      
      // Support design system colors for non-standard color values
      if (this.color && !['primary'].includes(this.color)) {
        // Set CSS custom properties for dynamic colors
        if (this.color.startsWith('#')) {
          style['--yz-tabs-active-color'] = this.color
        } else if (this.color.startsWith('var(')) {
          style['--yz-tabs-active-color'] = this.color
        } else if (this.color.startsWith('yz-')) {
          style['--yz-tabs-active-color'] = `var(--${this.color})`
        } else {
          style['--yz-tabs-active-color'] = `var(--yz-${this.color})`
        }
      }
      
      // Handle badge colors
      if (this.badgeColor) {
        if (this.badgeColor.startsWith('#')) {
          style['--yz-tabs-badge-color'] = this.badgeColor
        } else if (this.badgeColor.startsWith('var(')) {
          style['--yz-tabs-badge-color'] = this.badgeColor
        } else if (this.badgeColor.startsWith('yz-')) {
          style['--yz-tabs-badge-color'] = `var(--${this.badgeColor})`
        } else {
          style['--yz-tabs-badge-color'] = `var(--yz-${this.badgeColor})`
        }
      }
      
      if (this.badgeActiveColor) {
        if (this.badgeActiveColor.startsWith('#')) {
          style['--yz-tabs-badge-active-color'] = this.badgeActiveColor
        } else if (this.badgeActiveColor.startsWith('var(')) {
          style['--yz-tabs-badge-active-color'] = this.badgeActiveColor
        } else if (this.badgeActiveColor.startsWith('yz-')) {
          style['--yz-tabs-badge-active-color'] = `var(--${this.badgeActiveColor})`
        } else {
          style['--yz-tabs-badge-active-color'] = `var(--yz-${this.badgeActiveColor})`
        }
      }
      
      // Handle button hover color
      if (this.buttonHoverColor) {
        if (this.buttonHoverColor.startsWith('#')) {
          style['--yz-tabs-button-hover-color'] = this.buttonHoverColor
        } else if (this.buttonHoverColor.startsWith('var(')) {
          style['--yz-tabs-button-hover-color'] = this.buttonHoverColor
        } else if (this.buttonHoverColor.startsWith('yz-')) {
          style['--yz-tabs-button-hover-color'] = `var(--${this.buttonHoverColor})`
        } else {
          style['--yz-tabs-button-hover-color'] = `var(--yz-${this.buttonHoverColor})`
        }
      }
      
      return style
    }
  },
  provide() {
    return {
      yzTabsType: this.type,
      yzTabsColor: this.color
    }
  },
  methods: {
    handleChange(value) {
      this.$emit('input', value)
      this.$emit('change', value)
    }
  }
}
</script>

<style scoped>
/* Base YzTabs styles */
.yz-tabs {
  background: transparent;
}

/* Line type - traditional tabs with underline */
.yz-tabs--line {
  background: transparent;
}

.yz-tabs--line ::v-deep .v-tabs-bar {
  background: transparent !important;
  height: 44px;
}

.yz-tabs--line ::v-deep .v-tab {
  background: transparent;
  color: var(--yz-neutral-500);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  text-transform: none;
  letter-spacing: normal;
  min-width: auto;
  padding: 0 16px;
  height: 40px;
  border-radius: 0;
  margin: 0;
  transition: color 0.2s ease;
}

.yz-tabs--line ::v-deep .v-tab:not(.v-tab--active):not(.yz-tab--disabled):hover {
  color: var(--yz-neutral-500);
  position: relative;
}

.yz-tabs--line ::v-deep .v-tab:not(.v-tab--active):not(.yz-tab--disabled):hover::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: var(--yz-neutral-400);
  border-radius: 1px;
}

.yz-tabs--line ::v-deep .v-tab--active {
  color: var(--yz-neutral-700);
  font-weight: 600;
}

.yz-tabs--line ::v-deep .v-tabs-slider {
  height: 2px;
  background-color: var(--yz-neutral-700);
  border-radius: 1px;
  bottom: 0;
}

/* Button type - chip/button style tabs */
.yz-tabs--button {
  background: transparent;
}

.yz-tabs--button ::v-deep .v-tabs-bar {
  background: transparent !important;
  height: auto;
  padding: 4px;
}

.yz-tabs--button ::v-deep .v-tab {
  background: transparent;
  color: var(--yz-neutral-500);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  text-transform: none;
  letter-spacing: normal;
  min-width: auto;
  padding: 8px 10px;
  height: 36px;
  border-radius: 10px;
  margin: 0 4px;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.yz-tabs--button ::v-deep .v-tab:not(.yz-tab--disabled):hover {
  background: var(--yz-tabs-button-hover-color, var(--yz-neutral-200));
}

.yz-tabs--button ::v-deep .v-tab--active {
  background: var(--yz-neutral-300);
  color: var(--yz-neutral-700);
  font-weight: 600;
}

/* Hide slider for button type */
.yz-tabs--button ::v-deep .v-tabs-slider {
  display: none;
}

/* Primary color variants */
.yz-tabs--primary.yz-tabs--line ::v-deep .v-tabs-slider {
  background-color: var(--yz-neutral-700);
}

.yz-tabs--primary.yz-tabs--button ::v-deep .v-tab--active {
  background: var(--yz-neutral-300);
}

.yz-tabs--primary.yz-tabs--button ::v-deep .v-tab--active:hover {
  background: var(--yz-neutral-300);
}

/* Dynamic color support */
.yz-tabs--line ::v-deep .v-tabs-slider {
  background-color: var(--yz-tabs-active-color, var(--yz-neutral-700));
}

.yz-tabs--line ::v-deep .v-tab--active {
  color: var(--yz-tabs-active-color, var(--yz-neutral-700));
}

.yz-tabs--button ::v-deep .v-tab--active {
  background: var(--yz-tabs-active-color, var(--yz-neutral-300));
}

.yz-tabs--button ::v-deep .v-tab--active:hover {
  background: var(--yz-tabs-active-color, var(--yz-neutral-300));
}

/* Badge support - numbers inside tabs */
.yz-tabs ::v-deep .v-tab .yz-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  width: 18px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
  border-radius: 5px;
  background: var(--yz-tabs-badge-color, var(--yz-neutral-400));
  color: var(--yz-white);
  margin-left: 2px;
}

.yz-tabs ::v-deep .v-tab--active .yz-badge {
  background: var(--yz-tabs-badge-active-color, var(--yz-neutral-500));
}

.yz-tabs--button ::v-deep .v-tab--active .yz-badge {
  background: var(--yz-tabs-badge-active-color, var(--yz-neutral-500));
}

/* Icon support */
.yz-tabs ::v-deep .v-tab .v-icon {
  font-size: 16px;
  margin-right: 6px;
}

/* Disabled tab support */
.yz-tabs ::v-deep .v-tab--disabled,
.yz-tabs ::v-deep .v-tab[disabled],
.yz-tabs ::v-deep .yz-tab--disabled {
  cursor: not-allowed !important;
  pointer-events: auto !important;
  opacity: 0.6 !important;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .yz-tabs--line ::v-deep .v-tab {
    padding: 0 12px;
    font-size: 13px;
  }

  .yz-tabs--button ::v-deep .v-tab {
    padding: 6px 10px;
    font-size: 13px;
    margin: 0 2px;
  }
}
</style>